import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * How this visitor can install, if at all. There are three install routes and
 * only one of them is scriptable:
 *
 *   Chromium (Chrome, Edge, Samsung)  fires `beforeinstallprompt`  -> scriptable
 *   iOS / iPadOS Safari               Share -> Add to Home Screen  -> not scriptable
 *   macOS Safari (Sonoma+)            File -> Add to Dock          -> not scriptable
 *
 * The hook used to return a bare `isIOS` boolean alongside `canPrompt`, which
 * left macOS Safari with neither flag set: it is not iOS, and it never fires
 * `beforeinstallprompt`, so every branch gated on `canPrompt || isIOS` rendered
 * nothing at all and logged nothing. Returning one mode instead of two booleans
 * makes that gap impossible to express — a caller cannot show the Chromium
 * button to a Safari user, because there is no combination of flags that says so.
 */
export type InstallMode = 'ios' | 'macSafari' | 'prompt' | 'none';

/**
 * Verified against the iTunes lookup API: bundle com.paultristan.Leafnode, free,
 * iPhone and iPad. Deliberately no /au/ segment and no ?l= parameter — either
 * would pin every visitor to a single regional store.
 */
export const APP_STORE_URL = 'https://apps.apple.com/app/id6774088854';

const DISMISS_KEY = 'leafnode:install-prompt-dismissed';

/**
 * Safari in Private Browsing throws on any localStorage access — not just on
 * write. An uncaught throw here takes down whichever component the prompt is
 * mounted in, which on this site is the Header: the entire nav would disappear
 * because someone opened a private window. Both helpers swallow and carry on;
 * the cost of failing is a prompt that reappears, which is harmless.
 */
function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* private browsing — the prompt will return next visit, which is fine */
  }
}

/** iPadOS 13+ reports itself as a Mac; the touch-point count is the giveaway. */
export function detectIsIOS(nav: Navigator = navigator): boolean {
  const ua = nav.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)
  );
}

/**
 * Detected by elimination, and it has to be: every Chromium browser also puts
 * "Safari" in its user agent, so a naive /Safari/ test calls macOS Chrome
 * "Safari" and shows it Add-to-Dock instructions it does not have.
 *
 * Callers must evaluate iOS FIRST — an iPad satisfies /Macintosh/ too, and
 * maxTouchPoints is the only thing separating them.
 */
export function detectIsMacSafari(nav: Navigator = navigator): boolean {
  const ua = nav.userAgent;
  return (
    /Macintosh/.test(ua) &&
    nav.maxTouchPoints === 0 &&
    /Safari/.test(ua) &&
    !/Chrome|Chromium|Edg|OPR|CriOS|FxiOS|Firefox/.test(ua)
  );
}

/**
 * Already running as an installed app.
 *
 * `matchMedia('(display-mode: standalone)')` is unreliable on iOS, where
 * `navigator.standalone` is the one that actually reports it — so both are
 * checked rather than either alone.
 *
 * NOTE: there is no native-app case to detect here. Leafnode's iOS app is a
 * separate SwiftUI application with no WKWebView or SFSafariViewController
 * anywhere in it (checked across all 78 Swift files), so it never loads this
 * site. A Capacitor-style `isNativePlatform()` check — the shape used in
 * galacticvendingmachine, which IS a Capacitor app — would be permanently dead
 * code in this repo. If the app ever embeds a web view, mark it with a custom
 * user-agent token and test for that here.
 */
function detectInstalled(): boolean {
  const standaloneDisplay =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standaloneDisplay || iosStandalone;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'macSafari' | 'other'>('other');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // iOS before macSafari: an iPad claims to be a Macintosh.
    if (detectIsIOS()) setPlatform('ios');
    else if (detectIsMacSafari()) setPlatform('macSafari');

    if (detectInstalled()) setIsInstalled(true);
    setDismissed(readDismissed());

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => setIsInstalled(true);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') {
      setIsInstalled(true);
      return true;
    }
    return false;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    writeDismissed();
    setDismissed(true);
  }, []);

  const canPrompt = !!deferredPrompt;

  /**
   * iOS is offered the App Store, not Add-to-Home-Screen. Leafnode has a real
   * iOS app, and a home-screen web shortcut is strictly the worse option there:
   * no widgets, no App Store updates, and an icon indistinguishable from the
   * native app while behaving differently. The listing covers iPhone and iPad,
   * so iPadOS gets the same offer.
   *
   * macOS Safari is NOT offered the App Store: the listing is not verified for
   * macOS, so the app cannot run there and the installed web app is the only
   * option — not a lesser one.
   */
  let mode: InstallMode = 'none';
  if (!isInstalled && !dismissed) {
    if (platform === 'ios') mode = 'ios';
    else if (platform === 'macSafari') mode = 'macSafari';
    else if (canPrompt) mode = 'prompt';
  }

  return {
    mode,
    isInstalled,
    canPrompt,
    promptInstall,
    dismiss,
    appStoreUrl: APP_STORE_URL,
  };
}
